<template>
  <div class="nas-hw-page">
    <div class="nas-hw-content">
      <router-link to="/product/storage" class="nas-hw-back">← 返回存储产品</router-link>

      <div class="nas-hw-layout" :class="{ 'nas-hw-layout--table': !!specTable }">
        <div class="nas-hw-main">
          <header class="nas-hw-hero">
            <h1 class="nas-hw-title">{{ product?.name || '存储产品' }}</h1>
            <p v-if="product?.description" class="nas-hw-lead">{{ product.description }}</p>
          </header>

          <section class="nas-hw-spec">
            <!-- 技术白皮书 - 放在表格上方 -->
            <div v-if="whitepaper" class="nas-hw-whitepaper-section">
              <a
                :href="whitepaper.url"
                class="nas-hw-whitepaper-card"
                target="_blank"
                rel="noopener noreferrer"
                download
              >
                <div class="nas-hw-whitepaper-paper">
                  <div class="nas-hw-whitepaper-paper-header">
                    <img src="@/assets/logo1.svg" alt="瑞和云图" class="nas-hw-whitepaper-paper-logo">
                  </div>
                  <div class="nas-hw-whitepaper-paper-title" v-html="formatPaperTitle(whitepaper.label)"></div>
                </div>
                <div class="nas-hw-whitepaper-info">
                  <div class="nas-hw-whitepaper-title-text">{{ whitepaper.label }}</div>
                  <div class="nas-hw-whitepaper-btn">
                    <span>获取文档</span>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </div>
              </a>
            </div>

            <!-- 规格参数表格 -->
            <div
              v-if="specTable"
              class="nas-hw-matrix"
              :style="{ '--nas-col-count': activeColumnIndexes.length }"
            >
              <div class="nas-hw-matrix-toolbar">
                <button
                  v-for="group in specColumnGroups"
                  :key="group.key"
                  class="nas-hw-matrix-chip"
                  :class="{ 'is-active': activeGroupKey === group.key }"
                  type="button"
                  @click="activeGroupKey = group.key"
                >
                  {{ group.label }}
                </button>
              </div>

              <div class="nas-hw-matrix-columns">
                <!-- 第一列：对比内容指标 -->
                <div class="nas-hw-matrix-column nas-hw-matrix-column--label">
                  <div class="nas-hw-matrix-column-header">
                    <span>对比内容</span>
                  </div>
                  <div class="nas-hw-matrix-column-body">
                    <div
                      v-for="(row, idx) in specMatrixRows.slice(1)"
                      :key="row.label"
                      class="nas-hw-matrix-column-item"
                      :style="{ '--row-idx': idx }"
                    >
                      {{ row.label }}
                    </div>
                  </div>
                </div>

                <!-- 数据列 -->
                <div
                  v-for="(colIdx, colIndex) in activeColumnIndexes"
                  :key="colIdx"
                  class="nas-hw-matrix-column"
                  :class="`nas-hw-matrix-column--${colIndex % 4}`"
                >
                  <div class="nas-hw-matrix-column-header">
                    <span v-if="specMatrixRows[0]">
                      <strong>{{ specMatrixRows[0].values[colIdx] }}</strong>
                    </span>
                  </div>
                  <div class="nas-hw-matrix-column-body">
                    <div
                      v-for="(row, idx) in specMatrixRows.slice(1)"
                      :key="row.label"
                      class="nas-hw-matrix-column-item"
                      :style="{ '--row-idx': idx }"
                    >
                      <template v-if="row.values[colIdx] == null">-</template>
                      <template v-else>
                        <span
                          v-for="(part, partIdx) in splitCellValue(row.values[colIdx])"
                          :key="`${row.label}-${colIdx}-${partIdx}`"
                        >
                          {{ part }}
                        </span>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!specTable && specSectionParagraphs.length" class="nas-hw-fulltext">
              <p v-for="(line, i) in specSectionParagraphs" :key="i">{{ line }}</p>
            </div>
          </section>
        </div>

        <aside v-if="whitepaper && !specTable" class="nas-hw-aside">
          <div class="nas-hw-doc-card">
            <div class="nas-hw-doc-header">
              <img src="@/assets/logo1.svg" alt="瑞和云图" class="nas-hw-doc-logo">
            </div>
            <div class="nas-hw-doc-body">
              <div class="nas-hw-doc-type">技术白皮书</div>
              <div class="nas-hw-doc-title">{{ whitepaper.label }}</div>
            </div>
            <div class="nas-hw-doc-footer">
              <a
                :href="whitepaper.url"
                class="nas-hw-doc-btn"
                target="_blank"
                rel="noopener noreferrer"
                download
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 2V12M9 12L5 8M9 12L13 8M3 16H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                获取文档
              </a>
            </div>
          </div>
        </aside>
      </div>
    </div>


  </div>
</template>

<script>
import {
  getStorageLeafItems,
  storageRows,
  storageSpecMap,
  storageSpecTableBySlug,
  storageWhitepaperBySlug,
} from '@/data/nasStorageContent.js';

const HW_SLUGS = new Set(['xc-3000', 'xc-2000', 'xc-1000', 'xc-08']);

export default {
  name: 'NasStorageHardwareDetail',
  data() {
    return {
      activeGroupKey: null,
    };
  },
  beforeMount() {
    const s = this.$route.params.slug;
    if (!HW_SLUGS.has(s)) {
      this.$router.replace('/product/storage');
    }
  },
  mounted() {
    this.updateTitle();
  },
  computed: {
    slug() {
      return this.$route.params.slug;
    },
    product() {
      return getStorageLeafItems(storageRows).find((i) => i.slug === this.slug);
    },
    whitepaper() {
      return storageWhitepaperBySlug[this.slug] || null;
    },
    specTable() {
      return storageSpecTableBySlug[this.slug] || null;
    },
    specColumnGroups() {
      if (!this.specTable) return [];
      const groups = Array.isArray(this.specTable.columnGroups)
        ? this.specTable.columnGroups
        : [];
      if (groups.length) return groups;
      return [
        {
          key: 'all',
          label: '规格参数',
          indexes: this.specTable.columns.map((_, i) => i),
        },
      ];
    },
    activeColumnIndexes() {
      if (!this.specColumnGroups.length) return [];
      const group =
        this.specColumnGroups.find((g) => g.key === this.activeGroupKey) ||
        this.specColumnGroups[0];
      return group.indexes || [];
    },
    specMatrixRows() {
      if (!this.specTable) return [];
      return [{ label: '机型与机箱', values: this.specTable.columns }, ...this.specTable.rows];
    },
    specSectionParagraphs() {
      const routePath = `/product/storage/${this.slug}`;
      return storageSpecMap[routePath] || [];
    },
  },
  methods: {
    updateTitle() {
      const name = this.product?.name || '存储产品';
      document.title = `${name} | 瑞和云图`;
    },
    splitCellValue(value) {
      return String(value || '')
        .split(/\s*\/\s*|，\s*|\s{2,}/)
        .map((item) => item.trim())
        .filter(Boolean);
    },
    formatPaperTitle(label) {
      // 去掉"企业级"，并在"技术白皮书"前换行
      return label
        .replace('企业级', '')
        .replace('技术白皮书', '<br>技术白皮书');
    },
  },
  watch: {
    product() {
      this.updateTitle();
    },
    specColumnGroups: {
      immediate: true,
      handler(groups) {
        if (!groups.length) {
          this.activeGroupKey = '';
          return;
        }
        if (!groups.some((g) => g.key === this.activeGroupKey)) {
          this.activeGroupKey = groups[0].key;
        }
      },
    },
  },
};
</script>

<style scoped>
.nas-hw-page {
  --nas-link: #0056b3;
  --nas-muted: #6b7280;
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 2rem 0 0;
  font-family: system-ui, -apple-system, 'PingFang SC', 'Microsoft YaHei',
    sans-serif;
  background: #fff;
}

.nas-hw-content {
  width: min(1860px, calc(100% - 40px));
  max-width: none;
  margin: 0 auto;
  padding: 0 0 3.5rem;
}

.nas-hw-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 2.5rem 2rem;
  align-items: start;
}

.nas-hw-layout--table {
  grid-template-columns: minmax(0, 1fr);
}

@media (max-width: 960px) {
  .nas-hw-layout {
    grid-template-columns: 1fr;
  }
}

.nas-hw-main {
  min-width: 0;
}

.nas-hw-back {
  display: inline-block;
  font-size: 14px;
  color: var(--nas-link);
  text-decoration: none;
  margin-bottom: 1.25rem;
}

.nas-hw-back:hover {
  text-decoration: underline;
}

.nas-hw-hero {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e8e8e8;
}

.nas-hw-title {
  font-size: clamp(32px, 4.1vw, 48px);
  font-weight: 700;
  color: #172847;
  margin: 0 0 14px;
  line-height: 1.2;
}

.nas-hw-lead {
  margin: 0;
  font-size: 17px;
  line-height: 1.8;
  color: var(--nas-muted);
}

.nas-hw-spec-title {
  font-size: clamp(34px, 3.6vw, 46px);
  font-weight: 700;
  color: #1f2f54;
  margin: 0;
  letter-spacing: -0.01em;
}

.nas-hw-spec-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin: 0 0 20px;
}

.nas-hw-inline-download {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 10px;
  border: 1px solid rgba(59, 130, 246, 0.2);
  color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff, #dbeafe);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
}

.nas-hw-inline-download:hover {
  border-color: rgba(59, 130, 246, 0.4);
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.2);
}

.nas-hw-inline-download svg {
  width: 18px;
  height: 18px;
}

.nas-hw-matrix {
  border: none;
  background: transparent;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.nas-hw-matrix-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 0 20px;
}

.nas-hw-matrix-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  padding: 0 24px;
  border-radius: 21px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  background: #fff;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.nas-hw-matrix-chip:hover {
  color: #3b82f6;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.nas-hw-matrix-chip.is-active {
  color: #fff;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.35);
  transform: translateY(-2px);
}

.nas-hw-matrix-columns {
  display: grid;
  grid-template-columns: 140px repeat(var(--nas-col-count, 4), 1fr);
  gap: 0;
}

.nas-hw-matrix-column {
  display: contents;
}

.nas-hw-matrix-column-header {
  grid-row: 1;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nas-hw-matrix-column--label .nas-hw-matrix-column-header {
  background: #374151;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
  border-radius: 12px 0 0 0;
}

.nas-hw-matrix-column--0 .nas-hw-matrix-column-header {
  background: #3b6ea5;
}

.nas-hw-matrix-column--1 .nas-hw-matrix-column-header {
  background: #4a7c59;
}

.nas-hw-matrix-column--2 .nas-hw-matrix-column-header {
  background: #6a5a8a;
}

.nas-hw-matrix-column--3 .nas-hw-matrix-column-header {
  background: #8a6a5a;
}

.nas-hw-matrix-columns > .nas-hw-matrix-column:last-child .nas-hw-matrix-column-header {
  border-radius: 0 12px 0 0;
}

.nas-hw-matrix-column-header strong {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  display: block;
}

.nas-hw-matrix-column-body {
  display: contents;
}

.nas-hw-matrix-column-item {
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  min-height: 44px;
  display: flex;
  align-items: center;
  grid-row: calc(2 + var(--row-idx, 0));
}

.nas-hw-matrix-column--label .nas-hw-matrix-column-item {
  background: #374151;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.nas-hw-matrix-column--0 .nas-hw-matrix-column-item {
  background: #3b6ea5;
}

.nas-hw-matrix-column--1 .nas-hw-matrix-column-item {
  background: #4a7c59;
}

.nas-hw-matrix-column--2 .nas-hw-matrix-column-item {
  background: #6a5a8a;
}

.nas-hw-matrix-column--3 .nas-hw-matrix-column-item {
  background: #8a6a5a;
}

.nas-hw-matrix-column-item:last-child {
  border-bottom: none;
}

.nas-hw-matrix-column--label .nas-hw-matrix-column-item:last-child {
  border-radius: 0 0 0 12px;
}

.nas-hw-matrix-columns > .nas-hw-matrix-column:last-child .nas-hw-matrix-column-item:last-child {
  border-radius: 0 0 12px 0;
}

.nas-hw-matrix-column-item span {
  display: block;
}

@media (max-width: 768px) {
  .nas-hw-page {
    padding-top: 1.5rem;
  }

  .nas-hw-content {
    width: calc(100% - 20px);
    padding: 0 0 2.5rem;
  }

  .nas-hw-spec-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .nas-hw-spec-title {
    font-size: 30px;
  }

  .nas-hw-matrix-columns {
    flex-direction: column;
  }

  .nas-hw-matrix-column {
    flex: none;
    width: 100%;
  }

  .nas-hw-matrix-column--label {
    order: -1;
    flex: none;
    width: 100%;
  }
}

.nas-hw-fulltext {
  margin: 0;
  padding: 1rem 1.1rem;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #ffffff;
}

.nas-hw-fulltext p {
  margin: 0;
  font-size: 14px;
  line-height: 1.78;
  color: var(--nas-muted);
}

.nas-hw-fulltext p + p {
  margin-top: 6px;
}

.nas-hw-aside-card {
  position: sticky;
  top: 88px;
}

/* 技术白皮书区域 - 放在表格上方 */
.nas-hw-whitepaper-section {
  margin-bottom: 40px;
}

.nas-hw-whitepaper-card {
  display: inline-flex;
  align-items: center;
  gap: 32px;
  padding: 24px 32px 24px 28px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.nas-hw-whitepaper-card:hover {
  border-color: #bfdbfe;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.12);
  transform: translateY(-2px);
}

/* 左侧文档纸张 - 加大尺寸 */
.nas-hw-whitepaper-paper {
  width: 160px;
  height: 180px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 
    0 2px 6px rgba(0, 0, 0, 0.08),
    0 6px 16px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 0 0 12px;
  position: relative;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.nas-hw-whitepaper-card:hover .nas-hw-whitepaper-paper {
  transform: translateY(-4px) rotate(-1deg);
  box-shadow: 
    0 6px 16px rgba(0, 0, 0, 0.1),
    0 12px 28px rgba(0, 0, 0, 0.08);
}

/* 移除顶部渐变条，使用 header 背景 */

.nas-hw-whitepaper-paper-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%);
  padding: 16px 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 0 0 12px;
  border-radius: 6px 6px 0 0;
}

.nas-hw-whitepaper-paper-logo {
  width: 130px;
  min-width: 130px;
  max-width: none;
  height: auto;
  filter: brightness(0) invert(1);
}

.nas-hw-whitepaper-paper-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-align: center;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 右侧信息 */
.nas-hw-whitepaper-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.nas-hw-whitepaper-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.nas-hw-whitepaper-title-text {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  line-height: 1.4;
  max-width: 300px;
}

.nas-hw-whitepaper-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 24px;
  background: #2563eb;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  border-radius: 24px;
  margin-top: 10px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.nas-hw-whitepaper-btn svg {
  transition: transform 0.2s ease;
}

.nas-hw-whitepaper-card:hover .nas-hw-whitepaper-btn {
  background: #1d4ed8;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

.nas-hw-whitepaper-card:hover .nas-hw-whitepaper-btn svg {
  transform: translateX(2px);
}

@media (max-width: 640px) {
  .nas-hw-whitepaper-card {
    padding: 18px 22px;
    gap: 20px;
  }
  
  .nas-hw-whitepaper-paper {
    width: 120px;
    height: 150px;
    padding: 20px 12px;
  }
  
  .nas-hw-whitepaper-paper-logo {
    height: 28px;
  }
  
  .nas-hw-whitepaper-paper-title {
    font-size: 12px;
  }
  
  .nas-hw-whitepaper-title-text {
    font-size: 16px;
    max-width: 200px;
  }
}

/* 侧边栏版本保持竖向卡片样式 */
.nas-hw-doc-card {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  width: 280px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.nas-hw-doc-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.06);
}

.nas-hw-doc-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%);
  padding: 24px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
}

.nas-hw-doc-logo {
  width: 140px;
  height: auto;
  filter: brightness(0) invert(1);
}

.nas-hw-doc-body {
  padding: 24px 20px;
  text-align: center;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
}

.nas-hw-doc-type {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.nas-hw-doc-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.5;
}

.nas-hw-doc-footer {
  padding: 16px 20px 24px;
  display: flex;
  justify-content: center;
}

.nas-hw-doc-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.nas-hw-doc-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}
</style>
